import React from "react";
import { Helmet } from "react-helmet-async";

const SEO = ({
  title = "HexaPi Tech - Innovative Electronic & Software Solutions",
  description = "HexaPi Tech develops innovative and customized electronic and software solutions in Lebanon. From prototyping to deployment, we are your trusted technology partner.",
  image = "https://hexapi.tech/images/og-image.png",
  url = "https://hexapi.tech",
  type = "website",
  article = null,
}) => {
  const siteTitle = "HexaPi Tech";
  const fullTitle = title === siteTitle ? title : `${title} | ${siteTitle}`;

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "HexaPi Tech",
    url: "https://hexapi.tech",
    logo: "https://hexapi.tech/images/logo/logo.png",
    description: "Innovative electronic and software solutions company based in Lebanon.",
    address: {
      "@type": "PostalAddress",
      addressLocality: "West Bekaa",
      addressCountry: "Lebanon",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      availableLanguage: ["English", "Arabic"],
    },
    sameAs: [
      "https://www.facebook.com/HexaPiTech",
      "https://www.instagram.com/hexapitech",
      "https://www.linkedin.com/company/hexapitech",
    ],
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "HexaPi Tech",
    image: "https://hexapi.tech/images/logo/logo.png",
    url: "https://hexapi.tech",
    description: "Electronic and software solutions provider specializing in AI, robotics, web development, and automation.",
    address: {
      "@type": "PostalAddress",
      addressLocality: "West Bekaa",
      addressCountry: "Lebanon",
    },
    priceRange: "$$",
    openingHours: "Mo-Fr 09:00-18:00",
  };

  // Article/BlogPosting Schema (for blog posts)
  const articleSchema = article
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.date,
        author: {
          "@type": "Organization",
          name: "HexaPi Tech",
        },
        publisher: {
          "@type": "Organization",
          name: "HexaPi Tech",
          logo: {
            "@type": "ImageObject",
            url: "https://hexapi.tech/images/logo/logo.png",
          },
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": url,
        },
      }
    : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={siteTitle} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      {type === "website" && (
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      )}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
